import React, { useEffect, useState } from 'react';
import Background from '../components/Background';
import { Button, Input, Select } from '../components';
import { getUserProfile, updateUserProfile, updateUserPreferences, regenerateProfileContent } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Settings: React.FC = () => {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [style, setStyle] = useState<'western' | 'vedic'>('western');
  const [initialStyle, setInitialStyle] = useState<'western' | 'vedic'>('western');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState<'all' | 'cards' | 'planets' | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const res = await getUserProfile();
      setName(res.data.user?.name || '');
      setGender(res.data.user?.gender || '');
      const s = (res.data.prefs?.astrology_style || 'western') as 'western' | 'vedic';
      setStyle(s);
      setInitialStyle(s);
      setLoading(false);
    };
    load();
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile({ name, gender });
      await updateUserPreferences({ astrology_style: style });
      // If style changed, redirect to regenerating screen with toast
      if (style !== initialStyle) {
        navigate('/regenerating', { state: { style }, replace: true });
      } else {
        navigate('/user');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />
      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 sm:mb-8 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-heading text-gradient-gold tracking-luxury font-semibold">Settings</h1>
          <Button variant="secondary" onClick={() => navigate('/user')}>Back</Button>
        </div>
        {loading ? (
          <div className="text-text-secondary font-elegant tracking-luxury-tight text-sm sm:text-base">Loading…</div>
        ) : (
          <div className="space-y-4 animate-fade-in">
            <div 
              className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
              style={{
                borderColor: 'rgba(255, 184, 79, 0.2)',
                backgroundColor: 'rgba(20, 20, 32, 0.3)',
                boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="text-text-primary font-heading text-lg sm:text-xl mb-4 tracking-luxury-tight font-semibold">Profile</div>
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
                <Select
                  label="Gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  options={[
                    { value: '', label: 'Prefer not to say' },
                    { value: 'Female', label: 'Female' },
                    { value: 'Male', label: 'Male' },
                    { value: 'Other', label: 'Other' }
                  ]}
                />
              </div>
            </div>
            <div 
              className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
              style={{
                borderColor: 'rgba(255, 184, 79, 0.2)',
                backgroundColor: 'rgba(20, 20, 32, 0.3)',
                boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="text-text-primary font-heading text-lg sm:text-xl mb-4 tracking-luxury-tight font-semibold">Astrology Preference</div>
              <div className="flex gap-3">
                <button
                  className={`px-5 py-2.5 rounded-xl font-heading text-sm sm:text-base tracking-luxury-tight transition-all duration-300 backdrop-blur-xl ${
                    style === 'western' 
                      ? 'text-cosmic-gold border-2 scale-105' 
                      : 'text-text-secondary hover:text-text-primary border border-cosmic-gold/20 hover:border-cosmic-gold/40'
                  }`}
                  style={{
                    backgroundColor: style === 'western' 
                      ? 'rgba(255, 184, 79, 0.1)' 
                      : 'rgba(20, 20, 32, 0.3)',
                    borderColor: style === 'western' 
                      ? 'rgba(255, 184, 79, 0.5)' 
                      : 'rgba(255, 184, 79, 0.2)',
                    boxShadow: style === 'western' 
                      ? '0 0 20px rgba(255, 184, 79, 0.2)' 
                      : 'none'
                  }}
                  onClick={() => setStyle('western')}
                >
                  Western
                </button>
                <button
                  className={`px-5 py-2.5 rounded-xl font-heading text-sm sm:text-base tracking-luxury-tight transition-all duration-300 backdrop-blur-xl ${
                    style === 'vedic' 
                      ? 'text-cosmic-gold border-2 scale-105' 
                      : 'text-text-secondary hover:text-text-primary border border-cosmic-gold/20 hover:border-cosmic-gold/40'
                  }`}
                  style={{
                    backgroundColor: style === 'vedic' 
                      ? 'rgba(255, 184, 79, 0.1)' 
                      : 'rgba(20, 20, 32, 0.3)',
                    borderColor: style === 'vedic' 
                      ? 'rgba(255, 184, 79, 0.5)' 
                      : 'rgba(255, 184, 79, 0.2)',
                    boxShadow: style === 'vedic' 
                      ? '0 0 20px rgba(255, 184, 79, 0.2)' 
                      : 'none'
                  }}
                  onClick={() => setStyle('vedic')}
                >
                  Vedic
                </button>
              </div>
            </div>
            <div 
              className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
              style={{
                borderColor: 'rgba(255, 184, 79, 0.2)',
                backgroundColor: 'rgba(20, 20, 32, 0.3)',
                boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="text-text-primary font-heading text-lg sm:text-xl mb-2 tracking-luxury-tight font-semibold">Birth Data</div>
              <p className="text-text-secondary font-elegant text-sm sm:text-base mb-4 tracking-luxury-tight">
                Changing your birth data will recalculate your chart.
              </p>
              <Button variant="secondary" onClick={() => navigate('/onboarding')}>Edit Birth Info</Button>
            </div>
            <div 
              className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
              style={{
                borderColor: 'rgba(255, 184, 79, 0.2)',
                backgroundColor: 'rgba(20, 20, 32, 0.3)',
                boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="text-text-primary font-heading text-lg sm:text-xl mb-2 tracking-luxury-tight font-semibold">Regenerate Profile Content</div>
              <p className="text-text-secondary font-elegant text-sm sm:text-base mb-4 tracking-luxury-tight">
                Refresh your stored personality cards and planet blurbs using your current chart and style.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  onClick={async () => { setRegenLoading('all'); try { await regenerateProfileContent('all'); } finally { setRegenLoading(null); } }}
                  isLoading={regenLoading === 'all'}
                >
                  Regenerate All
                </Button>
                <Button
                  variant="secondary"
                  onClick={async () => { setRegenLoading('cards'); try { await regenerateProfileContent('cards'); } finally { setRegenLoading(null); } }}
                  isLoading={regenLoading === 'cards'}
                >
                  Cards Only
                </Button>
                <Button
                  variant="secondary"
                  onClick={async () => { setRegenLoading('planets'); try { await regenerateProfileContent('planets'); } finally { setRegenLoading(null); } }}
                  isLoading={regenLoading === 'planets'}
                >
                  Planets Only
                </Button>
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={onSave} isLoading={saving} variant="secondary">Save</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;


